import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CodeEditor } from '@/components/editor/CodeEditor';
import { FileExplorer } from '@/components/editor/FileExplorer';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import { VersionHistory } from '@/components/editor/VersionHistory';
import { editorAPI } from '@/lib/editor/api';
import { useAuth } from '@/contexts/AuthContext';
import { Maximize2, Minimize2, Monitor, Smartphone, Tablet, X } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export default function EditorDetailFull() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [fileTree, setFileTree] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<{ path: string; content: string } | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [previewMode, setPreviewMode] = useState<'development' | 'production'>('development');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionMessage, setVersionMessage] = useState('');
  
  // Panel widths
  const [leftPanelWidth, setLeftPanelWidth] = useState(250);
  const [rightPanelWidth, setRightPanelWidth] = useState(window.innerWidth * 0.4);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);
  
  // Local file storage for when backend storage is not available
  const [localFiles, setLocalFiles] = useState<Record<string, string>>({});
  const [filesLoaded, setFilesLoaded] = useState(false);

  // Determine entry file
  const entryFile = useMemo(() => {
    const fileKeys = Object.keys(localFiles);
    
    // Look for common entry points (without leading slashes)
    const mainPatterns = [
      'src/main.tsx', 'src/main.ts', 'src/main.jsx', 'src/main.js',
      'src/index.tsx', 'src/index.ts', 'src/index.jsx', 'src/index.js',
      'src/App.tsx', 'src/App.ts', 'src/App.jsx', 'src/App.js',
      'main.tsx', 'main.ts', 'main.jsx', 'main.js',
      'index.tsx', 'index.ts', 'index.jsx', 'index.js',
      'index.html',
      'App.tsx', 'App.ts', 'App.jsx', 'App.js'
    ];
    
    for (const pattern of mainPatterns) {
      if (fileKeys.includes(pattern)) {
        return pattern;
      }
    }
    
    // Check for component files if it's a template project
    const componentFiles = fileKeys.filter(f => 
      f.includes('components') && 
      (f.endsWith('.tsx') || f.endsWith('.jsx')) &&
      !f.includes('.test.') && !f.includes('.spec.') &&
      !f.includes('.stories.')
    );
    
    // If there's a single main component, it might be the entry
    if (componentFiles.length === 1) {
      return componentFiles[0];
    }
    
    // Look for any BusinessCard or main component
    const businessCard = componentFiles.find(f => 
      f.toLowerCase().includes('businesscard') || 
      f.toLowerCase().includes('main')
    );
    if (businessCard) {
      return businessCard;
    }
    
    // Fallback to first JS/JSX/TS/TSX file
    const jsFiles = fileKeys.filter(f => /\.(tsx?|jsx?)$/.test(f));
    return jsFiles[0] || 'index.js';
  }, [localFiles]);

  useEffect(() => {
    if (user && projectId) {
      loadProject();
      loadFileTree();
    }
  }, [projectId, user]);

  const loadProject = async () => {
    if (!projectId) {
      console.warn('Cannot load project: projectId is undefined');
      return;
    }

    try {
      const data = await editorAPI.getProject(projectId as string);
      setProject(data);
      
      // Check if project is in power mode and redirect
      if (data.mode === 'power') {
        // Preserve query params and ensure workspace is included
        const params = new URLSearchParams(searchParams);
        if (!params.has('ws') && data.workspaceId) {
          params.set('ws', data.workspaceId);
        }
        const queryString = params.toString();
        const redirectUrl = `/dashboard/editor/${projectId}/power${queryString ? `?${queryString}` : ''}`;
        navigate(redirectUrl, { replace: true });
        return;
      }
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  };

  const loadFileTree = async () => {
    if (!projectId) {
      console.warn('Cannot load file tree: projectId is undefined');
      return;
    }

    setFilesLoaded(false); // Mark as loading
    try {
      const tree = await editorAPI.getFileTree(projectId as string);
      setFileTree(tree);
      
      // Load all files into localFiles for preview
      await loadAllFiles(tree);
      setFilesLoaded(true); // Mark as loaded after all files are loaded
    } catch (error) {
      console.error('Failed to load file tree:', error);
      // Use local file tree if backend is not available
      const localTree = buildLocalFileTree(Object.keys(localFiles));
      setFileTree(localTree);
      setFilesLoaded(true); // Even on error, mark as loaded to show preview with local files
    }
  };

  const loadAllFiles = async (tree: any, path = '') => {
    if (tree.type === 'file') {
      const filePath = tree.path || path;
      try {
        const content = await editorAPI.getFileContent(projectId as string, filePath);
        // Always normalize paths - remove leading slash for consistency
        const normalizedPath = filePath.replace(/^\/+/, '');
        setLocalFiles(prev => ({ ...prev, [normalizedPath]: content.content }));
      } catch (error) {
        console.error(`Failed to load file ${filePath}:`, error);
      }
    } else if (tree.children) {
      for (const child of tree.children) {
        const childPath = path ? `${path}/${child.name}` : child.name;
        await loadAllFiles(child, childPath);
      }
    }
  };
  
  const buildLocalFileTree = (filePaths: string[]) => {
    const root = {
      name: 'root',
      type: 'directory' as const,
      children: [] as any[]
    };
    
    filePaths.forEach(path => {
      const parts = path.split('/').filter(Boolean);
      let current = root;
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isFile = i === parts.length - 1;
        
        if (isFile) {
          current.children.push({
            name: part,
            type: 'file',
            path: '/' + parts.join('/')
          });
        } else {
          let child = current.children.find(c => c.name === part && c.type === 'directory');
          if (!child) {
            child = {
              name: part,
              type: 'directory',
              children: []
            };
            current.children.push(child);
          }
          current = child;
        }
      }
    });
    
    return root;
  };

  const handleFileSelect = async (path: string) => {
    if (!projectId) {
      console.warn('Cannot select file: projectId is undefined');
      return;
    }

    try {
      const content = await editorAPI.getFileContent(projectId as string, path);
      setSelectedFile({ path, content: content.content });
    } catch (error) {
      console.error('Failed to load file:', error);
      // Use local content if backend is not available - normalize path for lookup
      const normalizedPath = path.replace(/^\/+/, '');
      const localContent = localFiles[normalizedPath] || localFiles[path] || '';
      setSelectedFile({ path, content: localContent });
    }
  };

  const handleFileCreate = async (filePath: string) => {
    if (!projectId) {
      console.warn('Cannot create file: projectId is undefined');
      return;
    }

    try {
      await editorAPI.createFile(projectId as string, filePath);
      await loadFileTree();
      await handleFileSelect(filePath);
    } catch (error) {
      console.error('Failed to create file:', error);
      // Create file locally if backend is not available - normalize path
      const normalizedPath = filePath.replace(/^\/+/, '');
      setLocalFiles(prev => ({ ...prev, [normalizedPath]: '' }));
      // Rebuild file tree with new file
      const localTree = buildLocalFileTree([...Object.keys(localFiles), normalizedPath]);
      setFileTree(localTree);
      setSelectedFile({ path: filePath, content: '' });
    }
  };

  const handleFileDelete = async (path: string) => {
    if (!projectId) {
      console.warn('Cannot delete file: projectId is undefined');
      return;
    }

    try {
      await editorAPI.deleteFile(projectId as string, path);
      await loadFileTree();
      if (selectedFile?.path === path) {
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      // Delete file locally if backend is not available - normalize path
      const normalizedPath = path.replace(/^\/+/, '');
      const newFiles = { ...localFiles };
      delete newFiles[normalizedPath];
      setLocalFiles(newFiles);
      // Rebuild file tree without deleted file
      const localTree = buildLocalFileTree(Object.keys(newFiles));
      setFileTree(localTree);
      if (selectedFile?.path === path) {
        setSelectedFile(null);
      }
    }
  };

  const handleFileRename = async (oldPath: string, newPath: string) => {
    // TODO: Implement rename functionality
    console.log('Rename:', oldPath, '->', newPath);
  };

  const handleCreateVersion = async () => {
    if (!versionMessage.trim()) return;

    try {
      await editorAPI.createVersion(projectId as string, versionMessage);
      if (showVersions) {
        // Refresh version history - component will handle this internally
      }
      setShowVersionModal(false);
      setVersionMessage('');
    } catch (error) {
      console.error('Failed to create version:', error);
    }
  };

  // Handle panel resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingLeft) {
        const newWidth = Math.max(200, Math.min(400, e.clientX));
        setLeftPanelWidth(newWidth);
      }
      if (isDraggingRight && showPreview) {
        const newWidth = Math.max(300, Math.min(window.innerWidth * 0.6, window.innerWidth - e.clientX));
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingLeft(false);
      setIsDraggingRight(false);
    };

    if (isDraggingLeft || isDraggingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingLeft, isDraggingRight, showPreview]);

  const getPreviewWidth = () => {
    if (!showPreview) return '0';
    
    switch (previewDevice) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      default:
        return '100%';
    }
  };

  if (!project || !fileTree) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <EditorToolbar
        project={project}
        onSaveVersion={() => setShowVersionModal(true)}
        onToggleVersions={() => setShowVersions(!showVersions)}
        showPreview={showPreview}
        onTogglePreview={() => setShowPreview(!showPreview)}
        previewMode={previewMode}
        onPreviewModeChange={setPreviewMode}
      />
      
      <div className="flex-1 flex overflow-hidden">
        {/* File Explorer */}
        <div 
          style={{ width: `${leftPanelWidth}px` }}
          className="relative flex-shrink-0"
        >
          <FileExplorer
            tree={fileTree}
            onFileSelect={handleFileSelect}
            onFileCreate={handleFileCreate}
            onFileDelete={handleFileDelete}
            onFileRename={handleFileRename}
            selectedFile={selectedFile?.path}
          />
          
          {/* Resize handle */}
          <div
            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-purple-500/50 transition-colors"
            onMouseDown={() => setIsDraggingLeft(true)}
          />
        </div>
        
        {/* Code Editor */}
        <div className="flex-1 min-w-0 flex">
          <div className={showPreview ? 'flex-1' : 'w-full'}>
            <CodeEditor
              projectId={projectId as string}
              file={selectedFile}
              onFileSave={(content: string) => {
                if (selectedFile) {
                  // Update local files storage - normalize path consistently
                  const normalizedPath = selectedFile.path.replace(/^\/+/, '');
                  setLocalFiles(prev => ({ ...prev, [normalizedPath]: content }));
                  // Update the selected file content to keep it in sync
                  setSelectedFile(prev => prev ? { ...prev, content } : null);
                }
              }}
            />
          </div>
        </div>
        
        {/* Preview Panel */}
        {showPreview && (
          <div 
            style={{ width: `${rightPanelWidth}px` }}
            className="relative flex-shrink-0 border-l border-border"
          >
            {/* Resize handle */}
            <div
              className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-purple-500/50 transition-colors z-10"
              onMouseDown={() => setIsDraggingRight(true)}
            />
            
            {/* Preview Toolbar */}
            <div className="h-10 border-b border-border bg-card flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`p-1.5 rounded transition-colors ${
                    previewDevice === 'desktop' 
                      ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400' 
                      : 'hover:bg-accent text-muted-foreground'
                  }`}
                  title="Desktop view"
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('tablet')}
                  className={`p-1.5 rounded transition-colors ${
                    previewDevice === 'tablet' 
                      ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400' 
                      : 'hover:bg-accent text-muted-foreground'
                  }`}
                  title="Tablet view"
                >
                  <Tablet className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`p-1.5 rounded transition-colors ${
                    previewDevice === 'mobile' 
                      ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400' 
                      : 'hover:bg-accent text-muted-foreground'
                  }`}
                  title="Mobile view"
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={() => setShowPreview(false)}
                className="p-1.5 hover:bg-accent rounded transition-colors text-muted-foreground"
                title="Close preview"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Preview Placeholder */}
            <div className="h-[calc(100%-2.5rem)] overflow-auto bg-gray-100 dark:bg-gray-950 flex justify-center">
              <div 
                style={{ width: getPreviewWidth() }}
                className="bg-white h-full flex items-center justify-center"
              >
                <div className="text-center p-8">
                  <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Preview not available in Lite mode</p>
                  <p className="text-sm text-gray-500">Switch to Power mode for live preview</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Version History (only show if preview is closed) */}
        {!showPreview && showVersions && (
          <div 
            style={{ width: `${300}px` }}
            className="relative flex-shrink-0"
          >
            <VersionHistory projectId={projectId as string} />
          </div>
        )}
      </div>

      {/* Version Creation Modal */}
      <Modal
        isOpen={showVersionModal}
        onClose={() => setShowVersionModal(false)}
        title="Create Version"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="version-message">Version Message</Label>
            <Input
              id="version-message"
              value={versionMessage}
              onChange={(e) => setVersionMessage(e.target.value)}
              placeholder="Describe the changes in this version"
              className="mt-1"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowVersionModal(false);
                setVersionMessage('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateVersion}
              disabled={!versionMessage.trim()}
              className="flex-1"
            >
              Create Version
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}